import React, { useEffect, useState } from 'react'
import { View } from 'react-native'

import useRoute from '@common/hooks/useRoute'
import useTheme from '@common/hooks/useTheme'
import { isSignAccountOpOverlayRoute } from '@common/modules/router/constants/signOverlayRoutes'
import { SignAccountOpControllerStateProvider } from '@web/contexts/signAccountOpControllerStateContext'
import useRequestsControllerState from '@web/hooks/useRequestsControllerState'
import SignAccountOpScreen from '@mobile/modules/sign-account-op/screens/SignAccountOpScreen'

// How long to keep the screen mounted after the request resolves — see below.
const UNMOUNT_GRACE_MS = 1500

/**
 * Mobile-web bridge for signing a stand-alone `calls` account-op (currently the
 * CCTP approve+depositForBurn burn). Sibling of SignMessageOverlay (§8c).
 *
 * On the extension a `calls` user request auto-opens a *separate* notification
 * window (getUiType().isRequestWindow), so the originating swap tab — and the
 * CCTP hook (useCCTPSwap) watching ActivityController.broadcastedButNotConfirmed
 * for the burn tx — stays mounted. The mobile-web SPA has a single window: the
 * only existing surface for such a request is the dashboard PendingRequestsBanner,
 * which route-navigates to `/sign-account-op` — that unmounts the swap screen and
 * kills the in-flight CCTP hook (flowRef lost → confirm-burn never fires).
 *
 * So instead of navigating, we render the very same @web-derived
 * SignAccountOpScreen on top of the swap screen. No route change → the swap
 * screen and its hook stay mounted underneath the whole time.
 *
 * Gated to the routes in `SIGN_ACCOUNT_OP_OVERLAY_ROUTES`: the dashboard keeps
 * its banner + `/sign-account-op` route flow unchanged. A `calls` request auto-becomes `currentUserRequest`
 * (RequestsController.build({type:'calls'}) → executionType 'open-request-window'),
 * so we just watch `currentUserRequest` — no banner-watching / manual
 * SET_CURRENT_REQUEST_BY_ID needed.
 *
 * Grace window: when the user signs, the controller broadcasts and clears
 * `currentUserRequest`, so the overlay wants to unmount immediately. We keep it
 * mounted briefly after the request clears to avoid tearing down the sign screen
 * mid-broadcast (matches SignMessageOverlay §8c); the underlying CCTP hook reads
 * the burn tx from ActivityController independently and is unaffected by the
 * overlay's lifecycle.
 *
 * Mobile-web-only by construction — mounted solely from Router.web.
 */
const SignAccountOpOverlay = () => {
  const { currentUserRequest } = useRequestsControllerState()
  const { theme } = useTheme()
  const { path } = useRoute()
  const pathname = path?.substring(1)

  // Surface the overlay on the screens that originate a stand-alone `calls`
  // request but have NO pending-requests banner of their own:
  //  - the three swap routes (CCTP approve+depositForBurn, and the PulseChain
  //    permit swap's wrap / approve / unwrap steps),
  //  - the in-app dApp browser + the WalletConnect screen, where an embedded /
  //    WalletConnect dApp's eth_sendTransaction (e.g. "Approve USDC for this
  //    contract") would otherwise arrive with no surface to sign it.
  // The dashboard and other routes keep their PendingRequestsBanner →
  // /sign-account-op flow unchanged. The list is shared with `usePermitSwap`,
  // which must not navigate away from exactly these routes.
  const isOnOverlayRoute = isSignAccountOpOverlayRoute(pathname)

  const hasCallsRequest = !!currentUserRequest && currentUserRequest.kind === 'calls'
  const shouldShow = hasCallsRequest && isOnOverlayRoute

  // Stays true through the grace window after the request clears so the sign
  // screen isn't torn down the instant the broadcast resolves.
  const [keepMounted, setKeepMounted] = useState(false)

  useEffect(() => {
    if (shouldShow) {
      setKeepMounted(true)
      return undefined
    }
    if (!keepMounted) return undefined
    const timer = setTimeout(() => setKeepMounted(false), UNMOUNT_GRACE_MS)
    return () => clearTimeout(timer)
  }, [shouldShow, keepMounted])

  if (!shouldShow && !keepMounted) return null

  // Opaque full-screen takeover: SignAccountOpScreen brings its own AppShell
  // chrome + page background. pointerEvents off during the grace window so the
  // (now request-less) screen can't be interacted with while it fades out.
  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        backgroundColor: theme.primaryBackground as string
      }}
      pointerEvents={shouldShow ? 'auto' : 'none'}
    >
      <SignAccountOpControllerStateProvider>
        <SignAccountOpScreen asOverlay />
      </SignAccountOpControllerStateProvider>
    </View>
  )
}

export default SignAccountOpOverlay
